import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMembresGerer, getMembreGerer, getFamillesGerer, getFamilleGerer, getRecolteursGerer, getRapportRecolteur, getCotisationsByMembre, getCotisationsFamilles, getEnfantsMembreGerer, createEnfantMembreGerer } from '../services/api';
import { setupConnectionListener, isOnline as checkIsOnline } from '../services/syncService';
import CarteMembre from '../components/CarteMembre';
import CarteFamille from '../components/CarteFamille';
import BonTraitement from '../components/BonTraitement';
import WebcamCapture from '../components/WebcamCapture';
import Footer from '../components/Footer';
import './DashboardGerer.css';

const DashboardGerer = () => {
  const navigate = useNavigate();
  const [gerer, setGerer] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('gerer_user') || '{}');
    } catch (error) {
      console.error('Erreur lors du parsing de gerer_user:', error);
      return {};
    }
  });
  const [membres, setMembres] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [recolteurs, setRecolteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cartes'); // 'cartes', 'rapports' ou 'liste'
  const [activeSubTab, setActiveSubTab] = useState('membres'); // 'membres' ou 'familles'
  const [selectedMembre, setSelectedMembre] = useState(null);
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [selectedRecolteur, setSelectedRecolteur] = useState(null);
  const [rapportRecolteur, setRapportRecolteur] = useState(null);
  const [loadingRapport, setLoadingRapport] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermFamilles, setSearchTermFamilles] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [searchRecolteurs, setSearchRecolteurs] = useState('');
  const [searchMembresRapport, setSearchMembresRapport] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageFamilles, setCurrentPageFamilles] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedPersonneBon, setSelectedPersonneBon] = useState(null);
  const [typePersonneBon, setTypePersonneBon] = useState(null); // 'membre' ou 'famille'
  const [cotisationsBon, setCotisationsBon] = useState([]);
  const [searchTermListe, setSearchTermListe] = useState('');
  const [activeSubTabListe, setActiveSubTabListe] = useState('membres'); // 'membres' ou 'familles'
  const [currentPageListe, setCurrentPageListe] = useState(1);
  const [currentPageListeFamilles, setCurrentPageListeFamilles] = useState(1);
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const [cachedMembres, setCachedMembres] = useState([]);
  const [cachedFamilles, setCachedFamilles] = useState([]);
  
  // États pour gérer les enfants des membres
  const [selectedMembreEnfants, setSelectedMembreEnfants] = useState(null);
  const [enfantsMembre, setEnfantsMembre] = useState([]);
  const [nouvelEnfant, setNouvelEnfant] = useState({ nom: '', prenom: '', date_naissance: '', lien: '' });
  const [photoEnfant, setPhotoEnfant] = useState(null);
  const [webcamKeyEnfant, setWebcamKeyEnfant] = useState(0);
  const [searchTermEnfants, setSearchTermEnfants] = useState('');
  const [currentPageEnfants, setCurrentPageEnfants] = useState(1);

  // Fonction pour ajouter un timeout à une promesse
  const withTimeout = (promise, timeoutMs = 10000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), timeoutMs)
      )
    ]);
  };

  // Fonction loadData avec useCallback pour éviter les problèmes de dépendances
  const loadData = useCallback(async () => {
    console.log('🔄 DashboardGerer - Début du chargement des données');
    
    // Timeout de sécurité : forcer setLoading(false) après 10 secondes
    const timeoutId = setTimeout(() => {
      console.warn('⏰ Timeout de sécurité - Arrêt du chargement après 10 secondes');
      setLoading(false);
    }, 10000);
    
    try {
      setLoading(true);
      
      if (checkIsOnline()) {
        console.log('🌐 Mode en ligne - Chargement depuis le serveur');
        // Mode en ligne : charger depuis le serveur
        try {
          console.log('📡 Appel API getMembresGerer, getFamillesGerer, getRecolteursGerer...');
          
          // Utiliser Promise.allSettled avec timeout pour chaque appel
          const [membresRes, famillesRes, recolteursRes] = await Promise.allSettled([
            withTimeout(getMembresGerer(), 8000).catch(err => {
              console.error('❌ Erreur getMembresGerer:', err);
              return { data: [] };
            }),
            withTimeout(getFamillesGerer(), 8000).catch(err => {
              console.error('❌ Erreur getFamillesGerer:', err);
              return { data: [] };
            }),
            withTimeout(getRecolteursGerer(), 8000).catch(err => {
              console.error('❌ Erreur getRecolteursGerer:', err);
              return { data: [] };
            })
          ]);
          
          // Extraire les données des résultats
          const membresData = membresRes.status === 'fulfilled' 
            ? (membresRes.value?.data || []) 
            : [];
          const famillesData = famillesRes.status === 'fulfilled' 
            ? (famillesRes.value?.data || []) 
            : [];
          const recolteursData = recolteursRes.status === 'fulfilled' 
            ? (recolteursRes.value?.data || []) 
            : [];
          
          console.log('✅ Données reçues:', {
            membres: membresData?.length || 0,
            familles: famillesData?.length || 0,
            recolteurs: recolteursData?.length || 0
          });
          
          setMembres(membresData || []);
          setFamilles(famillesData || []);
          setRecolteurs(recolteursData || []);
          
          // Mettre en cache pour le mode hors ligne
          if (membresData && membresData.length > 0) {
            localStorage.setItem('gerer_cached_membres', JSON.stringify(membresData));
            setCachedMembres(membresData);
          }
          if (famillesData && famillesData.length > 0) {
            localStorage.setItem('gerer_cached_familles', JSON.stringify(famillesData));
            setCachedFamilles(famillesData);
          }
        } catch (error) {
          console.error('❌ Erreur lors du chargement des données depuis le serveur:', error);
          // En cas d'erreur, utiliser les données en cache
          const cachedMembresData = localStorage.getItem('gerer_cached_membres');
          const cachedFamillesData = localStorage.getItem('gerer_cached_familles');
          
          if (cachedMembresData) {
            try {
              const parsed = JSON.parse(cachedMembresData);
              setMembres(parsed);
              setCachedMembres(parsed);
              console.log('📦 Utilisation des membres en cache:', parsed.length);
            } catch (e) {
              console.error('Erreur parsing membres cache:', e);
            }
          }
          if (cachedFamillesData) {
            try {
              const parsed = JSON.parse(cachedFamillesData);
              setFamilles(parsed);
              setCachedFamilles(parsed);
              console.log('📦 Utilisation des familles en cache:', parsed.length);
            } catch (e) {
              console.error('Erreur parsing familles cache:', e);
            }
          }
        }
      } else {
        // Mode hors ligne : utiliser les données en cache
        console.log('📴 Mode hors ligne - Utilisation des données en cache');
        const cachedMembresData = localStorage.getItem('gerer_cached_membres');
        const cachedFamillesData = localStorage.getItem('gerer_cached_familles');
        
        if (cachedMembresData) {
          try {
            const parsed = JSON.parse(cachedMembresData);
            setMembres(parsed);
            setCachedMembres(parsed);
            console.log('📦 Membres chargés depuis le cache:', parsed.length);
          } catch (e) {
            console.error('Erreur parsing membres cache:', e);
          }
        } else {
          setMembres([]);
        }
        
        if (cachedFamillesData) {
          try {
            const parsed = JSON.parse(cachedFamillesData);
            setFamilles(parsed);
            setCachedFamilles(parsed);
            console.log('📦 Familles chargées depuis le cache:', parsed.length);
          } catch (e) {
            console.error('Erreur parsing familles cache:', e);
          }
        } else {
          setFamilles([]);
        }
        
        setRecolteurs([]);
      }
    } catch (error) {
      console.error('❌ Erreur générale lors du chargement des données:', error);
      // En cas d'erreur, initialiser avec des tableaux vides
      setMembres([]);
      setFamilles([]);
      setRecolteurs([]);
    } finally {
      clearTimeout(timeoutId);
      console.log('✅ DashboardGerer - Fin du chargement, setLoading(false)');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🚀 DashboardGerer - useEffect déclenché');
    
    // Vérifier l'authentification
    const gererUser = localStorage.getItem('gerer_user');
    const gererToken = localStorage.getItem('gerer_token');
    
    console.log('🔐 Vérification authentification:', {
      hasUser: !!gererUser,
      hasToken: !!gererToken
    });
    
    if (!gererUser || !gererToken) {
      console.log('❌ Pas d\'authentification, redirection vers login');
      navigate('/login-gerer');
      return;
    }

    let parsedGerer;
    try {
      parsedGerer = JSON.parse(gererUser);
      console.log('👤 Utilisateur parsé:', { id: parsedGerer?.id, username: parsedGerer?.username });
      
      if (!parsedGerer || !parsedGerer.id) {
        console.log('❌ Pas d\'ID utilisateur, redirection vers login');
        navigate('/login-gerer');
        return;
      }
      setGerer(parsedGerer);
    } catch (error) {
      console.error('❌ Erreur lors du parsing de gerer_user:', error);
      navigate('/login-gerer');
      return;
    }
    
    // Initialiser le mode hors ligne
    const initOfflineMode = async () => {
      try {
        console.log('📦 Initialisation du mode hors ligne');
        
        // Charger les données mises en cache
        const cachedMembresData = localStorage.getItem('gerer_cached_membres');
        const cachedFamillesData = localStorage.getItem('gerer_cached_familles');
        
        if (cachedMembresData) {
          try {
            const parsed = JSON.parse(cachedMembresData);
            setCachedMembres(parsed);
            console.log('📦 Membres en cache chargés:', parsed.length);
          } catch (e) {
            console.error('Erreur lors du parsing des membres en cache:', e);
          }
        }
        if (cachedFamillesData) {
          try {
            const parsed = JSON.parse(cachedFamillesData);
            setCachedFamilles(parsed);
            console.log('📦 Familles en cache chargées:', parsed.length);
          } catch (e) {
            console.error('Erreur lors du parsing des familles en cache:', e);
          }
        }

        // Configurer l'écouteur de connexion
        setupConnectionListener((online) => {
          console.log('🌐 Statut de connexion changé:', online ? 'En ligne' : 'Hors ligne');
          setIsOnlineState(online);
          if (online) {
            // Recharger les données quand la connexion revient
            console.log('🔄 Reconnexion détectée, rechargement des données');
            loadData();
          }
        });
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation du mode hors ligne:', error);
      }
    };

    initOfflineMode();
    console.log('📥 Appel de loadData()');
    loadData();
  }, [navigate, loadData]);

  const handleLogout = () => {
    localStorage.removeItem('gerer_token');
    localStorage.removeItem('gerer_user');
    navigate('/login-gerer');
  };

  const handleImprimerCarte = async (membreId) => {
    try {
      if (checkIsOnline()) {
        // Mode en ligne : charger depuis le serveur
        const response = await getMembreGerer(membreId);
        setSelectedMembre(response.data);
      } else {
        // Mode hors ligne : utiliser les données en cache
        const membre = [...membres, ...cachedMembres].find(m => m.id === membreId);
        if (membre) {
          setSelectedMembre(membre);
        } else {
          alert('Membre non trouvé dans le cache. Veuillez vous connecter pour charger les données.');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du membre:', error);
      // Essayer avec les données en cache
      const membre = [...membres, ...cachedMembres].find(m => m.id === membreId);
      if (membre) {
        setSelectedMembre(membre);
      } else {
        alert('Erreur lors de la récupération du membre. Mode hors ligne : les données peuvent être limitées.');
      }
    }
  };

  const handleImprimerCarteFamille = async (familleId) => {
    try {
      if (checkIsOnline()) {
        // Mode en ligne : charger depuis le serveur
        const response = await getFamilleGerer(familleId);
        setSelectedFamille(response.data);
      } else {
        // Mode hors ligne : utiliser les données en cache
        const famille = [...familles, ...cachedFamilles].find(f => f.id === familleId);
        if (famille) {
          setSelectedFamille(famille);
        } else {
          alert('Famille non trouvée dans le cache. Veuillez vous connecter pour charger les données.');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la famille:', error);
      // Essayer avec les données en cache
      const famille = [...familles, ...cachedFamilles].find(f => f.id === familleId);
      if (famille) {
        setSelectedFamille(famille);
      } else {
        alert('Erreur lors de la récupération de la famille. Mode hors ligne : les données peuvent être limitées.');
      }
    }
  };

  // Fonctions pour gérer les enfants des membres
  const loadEnfantsMembre = async (membreId) => {
    try {
      const response = await getEnfantsMembreGerer(membreId);
      const enfants = response?.data || response || [];
      setEnfantsMembre(Array.isArray(enfants) ? enfants : []);
    } catch (error) {
      console.error('Erreur lors du chargement des enfants:', error);
      setEnfantsMembre([]);
    }
  };

  const handleSelectMembreEnfants = async (membre) => {
    setSelectedMembreEnfants(membre);
    await loadEnfantsMembre(membre.id);
  };

  const handlePhotoEnfantCapture = (file, fieldName) => {
    if (fieldName === 'photo') {
      setPhotoEnfant(file);
      console.log('📸 Photo de l\'enfant capturée:', file);
    }
  };

  const handleAddEnfantMembreSubmit = async () => {
    if (!nouvelEnfant.nom || !nouvelEnfant.prenom) {
      alert('Veuillez remplir au moins le nom et le prénom');
      return;
    }

    if (!selectedMembreEnfants) {
      alert('Aucun membre sélectionné');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('nom', nouvelEnfant.nom);
      formData.append('prenom', nouvelEnfant.prenom);
      if (nouvelEnfant.date_naissance) {
        formData.append('date_naissance', nouvelEnfant.date_naissance);
      }
      if (nouvelEnfant.lien) {
        formData.append('lien', nouvelEnfant.lien);
      }
      if (photoEnfant) {
        formData.append('photo', photoEnfant);
      }

      await createEnfantMembreGerer(selectedMembreEnfants.id, formData);
      alert('Enfant ajouté avec succès');
      
      // Réinitialiser le formulaire
      setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
      setPhotoEnfant(null);
      setWebcamKeyEnfant(prev => prev + 1);
      
      // Recharger les enfants
      await loadEnfantsMembre(selectedMembreEnfants.id);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'enfant:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'ajout de l\'enfant');
    }
  };


  const handleImprimerBonTraitement = async (personneId, type) => {
    try {
      if (type === 'membre') {
        if (checkIsOnline()) {
          const [membreResponse, cotisationsResponse] = await Promise.all([
            getMembreGerer(personneId),
            getCotisationsByMembre(personneId).catch(() => ({ data: [] }))
          ]);
          setSelectedPersonneBon(membreResponse.data);
          setTypePersonneBon('membre');
          setCotisationsBon(cotisationsResponse.data || []);
        } else {
          const membre = [...membres, ...cachedMembres].find(m => m.id === personneId);
          if (membre) {
            setSelectedPersonneBon(membre);
            setTypePersonneBon('membre');
            setCotisationsBon([]); // Pas de cotisations en mode hors ligne
          } else {
            alert('Membre non trouvé dans le cache. Veuillez vous connecter pour charger les données.');
          }
        }
      } else {
        if (checkIsOnline()) {
          const [familleResponse, cotisationsResponse] = await Promise.all([
            getFamilleGerer(personneId),
            getCotisationsFamilles({ famille_id: personneId }).catch(() => ({ data: [] }))
          ]);
          setSelectedPersonneBon(familleResponse.data);
          setTypePersonneBon('famille');
          setCotisationsBon(cotisationsResponse.data || []);
        } else {
          const famille = [...familles, ...cachedFamilles].find(f => f.id === personneId);
          if (famille) {
            setSelectedPersonneBon(famille);
            setTypePersonneBon('famille');
            setCotisationsBon([]); // Pas de cotisations en mode hors ligne
          } else {
            alert('Famille non trouvée dans le cache. Veuillez vous connecter pour charger les données.');
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération:', error);
      if (type === 'membre') {
        const membre = [...membres, ...cachedMembres].find(m => m.id === personneId);
        if (membre) {
          setSelectedPersonneBon(membre);
          setTypePersonneBon('membre');
          setCotisationsBon([]);
        }
      } else {
        const famille = [...familles, ...cachedFamilles].find(f => f.id === personneId);
        if (famille) {
          setSelectedPersonneBon(famille);
          setTypePersonneBon('famille');
          setCotisationsBon([]);
        }
      }
    }
  };

  const handleVoirRapport = async (recolteurId) => {
    try {
      setLoadingRapport(true);
      const response = await getRapportRecolteur(recolteurId, selectedDate);
      setRapportRecolteur(response.data);
      setSelectedRecolteur(recolteurs.find(r => r.id === recolteurId));
    } catch (error) {
      console.error('Erreur lors de la récupération du rapport:', error);
      alert('Erreur lors de la récupération du rapport');
    } finally {
      setLoadingRapport(false);
    }
  };

  const filteredMembres = membres.filter(membre => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      membre.numero_matricule?.toLowerCase().includes(search) ||
      membre.nom?.toLowerCase().includes(search) ||
      membre.postnom?.toLowerCase().includes(search) ||
      membre.prenom?.toLowerCase().includes(search) ||
      membre.telephone?.includes(search) ||
      membre.association?.toLowerCase().includes(search) ||
      membre.centre_sante?.toLowerCase().includes(search)
    );
  });

  const filteredFamilles = familles.filter(famille => {
    if (!searchTermFamilles) return true;
    const search = searchTermFamilles.toLowerCase();
    return (
      famille.numero_famille?.toLowerCase().includes(search) ||
      famille.nom_famille?.toLowerCase().includes(search) ||
      famille.chef_famille_nom?.toLowerCase().includes(search) ||
      famille.telephone?.includes(search) ||
      famille.commune?.toLowerCase().includes(search) ||
      famille.quartier?.toLowerCase().includes(search) ||
      famille.centre_sante?.toLowerCase().includes(search)
    );
  });

  // Calcul de la pagination pour les membres
  const totalPages = Math.ceil(filteredMembres.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMembres = filteredMembres.slice(indexOfFirstItem, indexOfLastItem);

  // Calcul de la pagination pour les familles
  const totalPagesFamilles = Math.ceil(filteredFamilles.length / itemsPerPage);
  const indexOfLastItemFamilles = currentPageFamilles * itemsPerPage;
  const indexOfFirstItemFamilles = indexOfLastItemFamilles - itemsPerPage;
  const currentFamilles = filteredFamilles.slice(indexOfFirstItemFamilles, indexOfLastItemFamilles);

  // Réinitialiser la page quand la recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPageFamilles(1);
  }, [searchTermFamilles]);

  // Afficher l'interface même si le chargement prend du temps (après 5 secondes)
  const [showInterface, setShowInterface] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('⏱️ Timeout d\'affichage - Affichage de l\'interface après 5 secondes');
      setShowInterface(true);
      setLoading(false); // Forcer l'arrêt du chargement
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  if (loading && !showInterface) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
        <p style={{ fontSize: '12px', marginTop: '10px', opacity: 0.7 }}>
          Si le chargement prend trop de temps, l'interface s'affichera automatiquement
        </p>
      </div>
    );
  }

  return (
    <div className="dashboard-gerer">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/logo.png" alt="Logo" className="header-logo" />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Gestion - Impression & Rapports</p>
            </div>
          </div>
          <div className="header-right">
            {/* Indicateur de statut de connexion */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              marginRight: '10px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: isOnlineState ? '#d4edda' : '#f8d7da',
              color: isOnlineState ? '#155724' : '#721c24',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {isOnlineState ? (
                <span>🌐 En ligne</span>
              ) : (
                <span>📴 Hors ligne</span>
              )}
            </div>
            <span>Bienvenue, {gerer.nom_complet || gerer.username}</span>
            <button onClick={handleLogout} className="btn btn-secondary">Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'cartes' ? 'active' : ''}`}
          onClick={() => setActiveTab('cartes')}
        >
          🎴 Gestion Impression Cartes
        </button>
        <button
          className={`tab ${activeTab === 'liste' ? 'active' : ''}`}
          onClick={() => setActiveTab('liste')}
        >
          📋 Bon de Traitement
        </button>
        <button
          className={`tab ${activeTab === 'rapports' ? 'active' : ''}`}
          onClick={() => setActiveTab('rapports')}
        >
          📊 Rapports Récolteurs
        </button>
        <button
          className={`tab ${activeTab === 'enfants' ? 'active' : ''}`}
          onClick={() => setActiveTab('enfants')}
        >
          👶 Enfants des Membres
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'cartes' && (
          <div className="cartes-section">
            <div className="section-header">
              <h2>Gestion de l'Impression des Cartes</h2>
              {!isOnlineState && (
                <div style={{
                  padding: '10px 15px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  marginBottom: '15px',
                  color: '#856404',
                  fontSize: '14px'
                }}>
                  ⚠️ <strong>Mode hors ligne</strong> : Vous utilisez les données mises en cache. Certaines fonctionnalités peuvent être limitées.
                </div>
              )}
              <div className="sub-tabs" style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                  className={`btn ${activeSubTab === 'membres' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveSubTab('membres')}
                >
                  🎴 Cartes de Membre ({filteredMembres.length})
                </button>
                <button
                  className={`btn ${activeSubTab === 'familles' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveSubTab('familles')}
                >
                  👨‍👩‍👧‍👦 Cartes de Famille ({filteredFamilles.length})
                </button>
              </div>
            </div>

            {activeSubTab === 'membres' && (
              <>
                <div className="section-header">
                  <h3>Gestion de l'Impression des Cartes de Membre ({filteredMembres.length} membres)</h3>
                  <div className="filters">
                    <input
                      type="text"
                      placeholder="Rechercher par nom, matricule, téléphone, association, centre de santé..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

            <div className="membres-table-container">
              <table className="membres-table">
                <thead>
                  <tr>
                    <th>Matricule</th>
                    <th>Nom complet</th>
                    <th>Téléphone</th>
                    <th>Association</th>
                    <th>Centre de santé</th>
                    <th>Date adhésion</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentMembres.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        Aucun membre trouvé
                      </td>
                    </tr>
                  ) : (
                    currentMembres.map(membre => (
                      <tr key={membre.id}>
                        <td data-label="Matricule:">{membre.numero_matricule}</td>
                        <td data-label="Nom complet:">{membre.nom} {membre.postnom || ''} {membre.prenom}</td>
                        <td data-label="Téléphone:">{membre.telephone || '-'}</td>
                        <td data-label="Association:">{membre.association || '-'}</td>
                        <td data-label="Centre de santé:">{membre.centre_sante || '-'}</td>
                        <td data-label="Date adhésion:">{new Date(membre.date_adhesion).toLocaleDateString('fr-FR')}</td>
                        <td>
                          <button
                            onClick={() => handleImprimerCarte(membre.id)}
                            className={`btn btn-sm ${membre.carte_payee ? 'btn-primary' : 'btn-warning'}`}
                            title={membre.carte_payee ? 'Imprimer la carte' : 'Imprimer la carte'}
                          >
                            🖨️ Imprimer
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredMembres.length > itemsPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredMembres.length)} sur {filteredMembres.length} membres
                </div>
                <div className="pagination-controls">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    ← Précédent
                  </button>
                  <div className="pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <button
                            key={page}
                            className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 3 || page === currentPage + 3) {
                        return <span key={page} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            )}
              </>
            )}

            {activeSubTab === 'familles' && (
              <>
                <div className="section-header">
                  <h3>Gestion de l'Impression des Cartes de Famille ({filteredFamilles.length} familles)</h3>
                  <div className="filters">
                    <input
                      type="text"
                      placeholder="Rechercher par numéro famille, nom famille, chef famille, téléphone, commune, quartier..."
                      value={searchTermFamilles}
                      onChange={(e) => setSearchTermFamilles(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="membres-table-container">
                  <table className="membres-table">
                    <thead>
                      <tr>
                        <th>Numéro Famille</th>
                        <th>Nom de Famille</th>
                        <th>Chef de Famille</th>
                        <th>Téléphone</th>
                        <th>Commune</th>
                        <th>Quartier</th>
                        <th>Date création</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentFamilles.length === 0 ? (
                        <tr>
                          <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                            Aucune famille trouvée
                          </td>
                        </tr>
                      ) : (
                        currentFamilles.map(famille => (
                          <tr key={famille.id}>
                            <td data-label="Numéro Famille:">{famille.numero_famille}</td>
                            <td data-label="Nom de Famille:">{famille.nom_famille || '-'}</td>
                            <td data-label="Chef de Famille:">{famille.chef_famille_nom || '-'}</td>
                            <td data-label="Téléphone:">{famille.telephone || '-'}</td>
                            <td data-label="Commune:">{famille.commune || '-'}</td>
                            <td data-label="Quartier:">{famille.quartier || '-'}</td>
                            <td data-label="Date création:">{new Date(famille.date_creation).toLocaleDateString('fr-FR')}</td>
                            <td>
                              <button
                                onClick={() => handleImprimerCarteFamille(famille.id)}
                                className={`btn btn-sm ${famille.carte_payee ? 'btn-primary' : 'btn-warning'}`}
                                title={famille.carte_payee ? 'Imprimer la carte' : 'Imprimer la carte'}
                              >
                                🖨️ Imprimer
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination pour les familles */}
                {filteredFamilles.length > itemsPerPage && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      Affichage de {indexOfFirstItemFamilles + 1} à {Math.min(indexOfLastItemFamilles, filteredFamilles.length)} sur {filteredFamilles.length} familles
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setCurrentPageFamilles(prev => Math.max(1, prev - 1))}
                        disabled={currentPageFamilles === 1}
                      >
                        ← Précédent
                      </button>
                      <div className="pagination-numbers">
                        {Array.from({ length: totalPagesFamilles }, (_, i) => i + 1).map(page => {
                          if (
                            page === 1 ||
                            page === totalPagesFamilles ||
                            (page >= currentPageFamilles - 2 && page <= currentPageFamilles + 2)
                          ) {
                            return (
                              <button
                                key={page}
                                className={`btn btn-sm ${currentPageFamilles === page ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setCurrentPageFamilles(page)}
                              >
                                {page}
                              </button>
                            );
                          } else if (page === currentPageFamilles - 3 || page === currentPageFamilles + 3) {
                            return <span key={page} className="pagination-ellipsis">...</span>;
                          }
                          return null;
                        })}
                      </div>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setCurrentPageFamilles(prev => Math.min(totalPagesFamilles, prev + 1))}
                        disabled={currentPageFamilles === totalPagesFamilles}
                      >
                        Suivant →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'rapports' && (
          <div className="rapports-section">
            <div className="section-header">
              <h2>Rapports par Récolteur</h2>
              <div className="filters">
                <input
                  type="text"
                  placeholder="🔍 Rechercher un récolteur (nom, matricule, association, commune)..."
                  value={searchRecolteurs}
                  onChange={(e) => setSearchRecolteurs(e.target.value)}
                  className="search-input"
                  style={{ flex: 1, minWidth: '300px' }}
                />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="date-input"
                  placeholder="Filtrer par date (optionnel)"
                />
                <button
                  onClick={() => {
                    setSelectedDate('');
                    setSearchRecolteurs('');
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  Réinitialiser
                </button>
              </div>
            </div>

            {searchRecolteurs && (
              <div className="search-info-recolteurs">
                <span>
                  {recolteurs.filter(recolteur => {
                    if (!searchRecolteurs) return true;
                    const search = searchRecolteurs.toLowerCase();
                    return (
                      recolteur.nom_complet?.toLowerCase().includes(search) ||
                      recolteur.matricule?.toLowerCase().includes(search) ||
                      recolteur.association?.toLowerCase().includes(search) ||
                      recolteur.commune?.toLowerCase().includes(search) ||
                      recolteur.centre_sante?.toLowerCase().includes(search) ||
                      recolteur.username?.toLowerCase().includes(search)
                    );
                  }).length} récolteur{recolteurs.filter(recolteur => {
                    if (!searchRecolteurs) return true;
                    const search = searchRecolteurs.toLowerCase();
                    return (
                      recolteur.nom_complet?.toLowerCase().includes(search) ||
                      recolteur.matricule?.toLowerCase().includes(search) ||
                      recolteur.association?.toLowerCase().includes(search) ||
                      recolteur.commune?.toLowerCase().includes(search) ||
                      recolteur.centre_sante?.toLowerCase().includes(search) ||
                      recolteur.username?.toLowerCase().includes(search)
                    );
                  }).length > 1 ? 's' : ''} trouvé{recolteurs.filter(recolteur => {
                    if (!searchRecolteurs) return true;
                    const search = searchRecolteurs.toLowerCase();
                    return (
                      recolteur.nom_complet?.toLowerCase().includes(search) ||
                      recolteur.matricule?.toLowerCase().includes(search) ||
                      recolteur.association?.toLowerCase().includes(search) ||
                      recolteur.commune?.toLowerCase().includes(search) ||
                      recolteur.centre_sante?.toLowerCase().includes(search) ||
                      recolteur.username?.toLowerCase().includes(search)
                    );
                  }).length > 1 ? 's' : ''} sur {recolteurs.length}
                </span>
              </div>
            )}

            <div className="recolteurs-grid">
              {(() => {
                const filteredRecolteurs = recolteurs.filter(recolteur => {
                  if (!searchRecolteurs) return true;
                  const search = searchRecolteurs.toLowerCase();
                  return (
                    recolteur.nom_complet?.toLowerCase().includes(search) ||
                    recolteur.matricule?.toLowerCase().includes(search) ||
                    recolteur.association?.toLowerCase().includes(search) ||
                    recolteur.commune?.toLowerCase().includes(search) ||
                    recolteur.centre_sante?.toLowerCase().includes(search) ||
                    recolteur.username?.toLowerCase().includes(search)
                  );
                });

                if (filteredRecolteurs.length === 0) {
                  return (
                    <div className="no-recolteurs-found">
                      <p>Aucun récolteur trouvé pour "{searchRecolteurs}"</p>
                      <button
                        onClick={() => setSearchRecolteurs('')}
                        className="btn btn-secondary btn-sm"
                        style={{ marginTop: '10px' }}
                      >
                        Effacer la recherche
                      </button>
                    </div>
                  );
                }

                return filteredRecolteurs.map(recolteur => (
                <div key={recolteur.id} className="recolteur-card">
                  <div className="recolteur-card-header">
                    <h3>{recolteur.nom_complet}</h3>
                    <span className="recolteur-matricule">{recolteur.matricule || '-'}</span>
                  </div>
                  <div className="recolteur-card-body">
                    <p><strong>Association:</strong> {recolteur.association || '-'}</p>
                    <p><strong>Commune:</strong> {recolteur.commune || '-'}</p>
                    <p><strong>Centre de santé:</strong> {recolteur.centre_sante || '-'}</p>
                  </div>
                  <div className="recolteur-card-footer">
                    <button
                      onClick={() => handleVoirRapport(recolteur.id)}
                      className="btn btn-primary"
                      disabled={loadingRapport}
                    >
                      {loadingRapport ? 'Chargement...' : '📊 Voir Rapport'}
                    </button>
                  </div>
                </div>
                ));
              })()}
            </div>

            {rapportRecolteur && selectedRecolteur && (
              <div className="rapport-modal">
                <div className="rapport-content">
                  <div className="rapport-header">
                    <h2>Rapport détaillé - {selectedRecolteur.nom_complet}</h2>
                    <button onClick={() => setRapportRecolteur(null)} className="close-btn">×</button>
                  </div>
                  
                  <div className="rapport-body">
                    <div className="stats-grid">
                      <div className="stat-card">
                        <h3>Statistiques Globales</h3>
                        <div className="stat-item">
                          <span>Total Membres:</span>
                          <strong>{rapportRecolteur.statistiques.globales.totalMembres}</strong>
                        </div>
                        <div className="stat-item">
                          <span>Montant Total Récolté:</span>
                          <strong>{rapportRecolteur.statistiques.globales.totalMontantRecolte.toLocaleString('fr-FR')} CDF</strong>
                        </div>
                        <div className="stat-item">
                          <span>Total Paiements:</span>
                          <strong>{rapportRecolteur.statistiques.globales.totalPaiements}</strong>
                        </div>
                        <div className="stat-item">
                          <span>Cotisations Payées:</span>
                          <strong>{rapportRecolteur.statistiques.globales.cotisationsPayees}</strong>
                        </div>
                        <div className="stat-item">
                          <span>Cotisations Impayées:</span>
                          <strong>{rapportRecolteur.statistiques.globales.cotisationsImpayees}</strong>
                        </div>
                        <div className="stat-item">
                          <span>Cartes Payées:</span>
                          <strong>{rapportRecolteur.statistiques.globales.totalCartesPayees}</strong>
                        </div>
                        <div className="stat-item">
                          <span>Montant Total Cartes:</span>
                          <strong>{rapportRecolteur.statistiques.globales.montantTotalCartes.toLocaleString('fr-FR')} CDF</strong>
                        </div>
                      </div>

                      {rapportRecolteur.statistiques.journalieres && (
                        <div className="stat-card">
                          <h3>Statistiques Journalières</h3>
                          <div className="stat-item">
                            <span>Date:</span>
                            <strong>{rapportRecolteur.statistiques.journalieres.date}</strong>
                          </div>
                          <div className="stat-item">
                            <span>Membres Aujourd'hui:</span>
                            <strong>{rapportRecolteur.statistiques.journalieres.membresAujourdhui}</strong>
                          </div>
                          <div className="stat-item">
                            <span>Montant Récolté Aujourd'hui:</span>
                            <strong>{rapportRecolteur.statistiques.journalieres.montantRecolteAujourdhui.toLocaleString('fr-FR')} CDF</strong>
                          </div>
                          <div className="stat-item">
                            <span>Paiements Aujourd'hui:</span>
                            <strong>{rapportRecolteur.statistiques.journalieres.nombrePaiementsAujourdhui}</strong>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="membres-list">
                      <div className="membres-list-header">
                        <h3>Liste des Membres ({rapportRecolteur.membres.length})</h3>
                        {rapportRecolteur.membres.length > 0 && (
                          <div className="membres-summary">
                            <span>Total récolté: <strong>{rapportRecolteur.membres.reduce((sum, m) => sum + (m.montant_total || 0), 0).toLocaleString('fr-FR')} CDF</strong></span>
                            <span>Total paiements: <strong>{rapportRecolteur.membres.reduce((sum, m) => sum + (m.total_paiements || 0), 0)}</strong></span>
                          </div>
                        )}
                      </div>
                      {rapportRecolteur.membres.length > 0 && (
                        <div className="membres-search-container">
                          <input
                            type="text"
                            placeholder="🔍 Rechercher un membre (matricule, nom, téléphone, association)..."
                            value={searchMembresRapport}
                            onChange={(e) => setSearchMembresRapport(e.target.value)}
                            className="search-input membres-search"
                          />
                          {searchMembresRapport && (
                            <button
                              onClick={() => setSearchMembresRapport('')}
                              className="btn-clear-search"
                              title="Effacer la recherche"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      )}
                      <div className="table-container">
                        {(() => {
                          const filteredMembres = rapportRecolteur.membres.filter(membre => {
                            if (!searchMembresRapport) return true;
                            const search = searchMembresRapport.toLowerCase();
                            return (
                              membre.numero_matricule?.toLowerCase().includes(search) ||
                              membre.nom?.toLowerCase().includes(search) ||
                              membre.postnom?.toLowerCase().includes(search) ||
                              membre.prenom?.toLowerCase().includes(search) ||
                              membre.telephone?.includes(search) ||
                              membre.association?.toLowerCase().includes(search) ||
                              membre.centre_sante?.toLowerCase().includes(search)
                            );
                          });

                          if (filteredMembres.length === 0) {
                            return (
                              <div className="no-data-message">
                                <p>
                                  {searchMembresRapport 
                                    ? `Aucun membre trouvé pour "${searchMembresRapport}"`
                                    : 'Aucun membre enregistré par ce récolteur.'
                                  }
                                </p>
                                {searchMembresRapport && (
                                  <button
                                    onClick={() => setSearchMembresRapport('')}
                                    className="btn btn-secondary btn-sm"
                                    style={{ marginTop: '10px' }}
                                  >
                                    Effacer la recherche
                                  </button>
                                )}
                              </div>
                            );
                          }

                          return (
                            <>
                              <table className="rapport-table membres-table">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Matricule</th>
                                    <th>Nom Complet</th>
                                    <th>Téléphone</th>
                                    <th>Association</th>
                                    <th>Centre de Santé</th>
                                    <th>Date Adhésion</th>
                                    <th>Nb. Paiements</th>
                                    <th>Montant Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredMembres.map((membre, index) => (
                                <tr key={membre.id}>
                                  <td className="index-cell">{index + 1}</td>
                                  <td className="matricule-cell">
                                    <strong>{membre.numero_matricule}</strong>
                                  </td>
                                  <td className="nom-cell">
                                    {membre.nom} {membre.postnom || ''} {membre.prenom}
                                  </td>
                                  <td>{membre.telephone || '-'}</td>
                                  <td>{membre.association || '-'}</td>
                                  <td>{membre.centre_sante || '-'}</td>
                                  <td>{new Date(membre.date_adhesion).toLocaleDateString('fr-FR')}</td>
                                  <td className="paiements-cell">
                                    <span className="badge badge-info">{membre.total_paiements || 0}</span>
                                  </td>
                                  <td className="montant-cell">
                                    <strong>{parseFloat(membre.montant_total || 0).toLocaleString('fr-FR')} CDF</strong>
                                  </td>
                                  </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="table-footer">
                                    <td colSpan="7" className="footer-label">
                                      <strong>
                                        {searchMembresRapport 
                                          ? `TOTAL (${filteredMembres.length} résultat${filteredMembres.length > 1 ? 's' : ''})`
                                          : 'TOTAL'
                                        }
                                      </strong>
                                    </td>
                                    <td className="footer-value">
                                      <strong>{filteredMembres.reduce((sum, m) => sum + (m.total_paiements || 0), 0)}</strong>
                                    </td>
                                    <td className="footer-value montant-total">
                                      <strong>{filteredMembres.reduce((sum, m) => sum + parseFloat(m.montant_total || 0), 0).toLocaleString('fr-FR')} CDF</strong>
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                              {searchMembresRapport && (
                                <div className="search-results-info">
                                  <span>
                                    {filteredMembres.length} membre{filteredMembres.length > 1 ? 's' : ''} trouvé{filteredMembres.length > 1 ? 's' : ''} sur {rapportRecolteur.membres.length}
                                  </span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'liste' && (
          <div className="liste-section">
            <div className="section-header">
              <h2>Bon de Traitement</h2>
              {!isOnlineState && (
                <div style={{
                  padding: '10px 15px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  marginBottom: '15px',
                  color: '#856404',
                  fontSize: '14px'
                }}>
                  ⚠️ <strong>Mode hors ligne</strong> : Vous utilisez les données mises en cache. Certaines fonctionnalités peuvent être limitées.
                </div>
              )}
              <div className="sub-tabs" style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                  className={`btn ${activeSubTabListe === 'membres' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveSubTabListe('membres')}
                >
                  👤 Membres ({membres.length})
                </button>
                <button
                  className={`btn ${activeSubTabListe === 'familles' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveSubTabListe('familles')}
                >
                  👨‍👩‍👧‍👦 Familles ({familles.length})
                </button>
              </div>
            </div>

            {activeSubTabListe === 'membres' && (
              <>
                <div className="section-header">
                  <h3>Liste des Membres ({membres.length} membres)</h3>
                  <div className="filters">
                    <input
                      type="text"
                      placeholder="Rechercher par nom, matricule, téléphone, association, centre de santé..."
                      value={searchTermListe}
                      onChange={(e) => {
                        setSearchTermListe(e.target.value);
                        setCurrentPageListe(1);
                      }}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="membres-table-container">
                  <table className="membres-table">
                    <thead>
                      <tr>
                        <th>Matricule</th>
                        <th>Nom complet</th>
                        <th>Téléphone</th>
                        <th>Association</th>
                        <th>Centre de santé</th>
                        <th>Date adhésion</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = membres.filter(membre => {
                          if (!searchTermListe) return true;
                          const search = searchTermListe.toLowerCase();
                          return (
                            membre.numero_matricule?.toLowerCase().includes(search) ||
                            membre.nom?.toLowerCase().includes(search) ||
                            membre.postnom?.toLowerCase().includes(search) ||
                            membre.prenom?.toLowerCase().includes(search) ||
                            membre.telephone?.includes(search) ||
                            membre.association?.toLowerCase().includes(search) ||
                            membre.centre_sante?.toLowerCase().includes(search)
                          );
                        });

                        const totalPagesListe = Math.ceil(filtered.length / itemsPerPage);
                        const indexOfLastItemListe = currentPageListe * itemsPerPage;
                        const indexOfFirstItemListe = indexOfLastItemListe - itemsPerPage;
                        const currentItems = filtered.slice(indexOfFirstItemListe, indexOfLastItemListe);

                        return (
                          <>
                            {currentItems.length === 0 ? (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                  Aucun membre trouvé
                                </td>
                              </tr>
                            ) : (
                              currentItems.map(membre => (
                                <tr key={membre.id}>
                                  <td data-label="Matricule:">{membre.numero_matricule}</td>
                                  <td data-label="Nom complet:">{membre.nom} {membre.postnom || ''} {membre.prenom}</td>
                                  <td data-label="Téléphone:">{membre.telephone || '-'}</td>
                                  <td data-label="Association:">{membre.association || '-'}</td>
                                  <td data-label="Centre de santé:">{membre.centre_sante || '-'}</td>
                                  <td data-label="Date adhésion:">{new Date(membre.date_adhesion).toLocaleDateString('fr-FR')}</td>
                                  <td>
                                    <button
                                      onClick={() => handleImprimerBonTraitement(membre.id, 'membre')}
                                      className="btn btn-sm btn-primary"
                                      title="Imprimer le bon de traitement"
                                    >
                                      🖨️ Bon de Traitement
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {(() => {
                  const filtered = membres.filter(membre => {
                    if (!searchTermListe) return true;
                    const search = searchTermListe.toLowerCase();
                    return (
                      membre.numero_matricule?.toLowerCase().includes(search) ||
                      membre.nom?.toLowerCase().includes(search) ||
                      membre.postnom?.toLowerCase().includes(search) ||
                      membre.prenom?.toLowerCase().includes(search) ||
                      membre.telephone?.includes(search) ||
                      membre.association?.toLowerCase().includes(search) ||
                      membre.centre_sante?.toLowerCase().includes(search)
                    );
                  });
                  const totalPagesListe = Math.ceil(filtered.length / itemsPerPage);
                  const indexOfLastItemListe = currentPageListe * itemsPerPage;
                  const indexOfFirstItemListe = indexOfLastItemListe - itemsPerPage;

                  if (filtered.length > itemsPerPage) {
                    return (
                      <div className="pagination-container">
                        <div className="pagination-info">
                          Affichage de {indexOfFirstItemListe + 1} à {Math.min(indexOfLastItemListe, filtered.length)} sur {filtered.length} membres
                        </div>
                        <div className="pagination-controls">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setCurrentPageListe(prev => Math.max(1, prev - 1))}
                            disabled={currentPageListe === 1}
                          >
                            ← Précédent
                          </button>
                          <div className="pagination-numbers">
                            {Array.from({ length: totalPagesListe }, (_, i) => i + 1).map(page => {
                              if (
                                page === 1 ||
                                page === totalPagesListe ||
                                (page >= currentPageListe - 2 && page <= currentPageListe + 2)
                              ) {
                                return (
                                  <button
                                    key={page}
                                    className={`btn btn-sm ${currentPageListe === page ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setCurrentPageListe(page)}
                                  >
                                    {page}
                                  </button>
                                );
                              } else if (page === currentPageListe - 3 || page === currentPageListe + 3) {
                                return <span key={page} className="pagination-ellipsis">...</span>;
                              }
                              return null;
                            })}
                          </div>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setCurrentPageListe(prev => Math.min(totalPagesListe, prev + 1))}
                            disabled={currentPageListe === totalPagesListe}
                          >
                            Suivant →
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}

            {activeSubTabListe === 'familles' && (
              <>
                <div className="section-header">
                  <h3>Liste des Familles ({familles.length} familles)</h3>
                  <div className="filters">
                    <input
                      type="text"
                      placeholder="Rechercher par numéro famille, nom famille, chef famille, téléphone, commune, quartier..."
                      value={searchTermListe}
                      onChange={(e) => {
                        setSearchTermListe(e.target.value);
                        setCurrentPageListeFamilles(1);
                      }}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="membres-table-container">
                  <table className="membres-table">
                    <thead>
                      <tr>
                        <th>Numéro Famille</th>
                        <th>Nom de Famille</th>
                        <th>Chef de Famille</th>
                        <th>Téléphone</th>
                        <th>Commune</th>
                        <th>Quartier</th>
                        <th>Date création</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = familles.filter(famille => {
                          if (!searchTermListe) return true;
                          const search = searchTermListe.toLowerCase();
                          return (
                            famille.numero_famille?.toLowerCase().includes(search) ||
                            famille.nom_famille?.toLowerCase().includes(search) ||
                            famille.chef_famille_nom?.toLowerCase().includes(search) ||
                            famille.telephone?.includes(search) ||
                            famille.commune?.toLowerCase().includes(search) ||
                            famille.quartier?.toLowerCase().includes(search) ||
                            famille.centre_sante?.toLowerCase().includes(search)
                          );
                        });

                        const totalPagesListeFamilles = Math.ceil(filtered.length / itemsPerPage);
                        const indexOfLastItemListeFamilles = currentPageListeFamilles * itemsPerPage;
                        const indexOfFirstItemListeFamilles = indexOfLastItemListeFamilles - itemsPerPage;
                        const currentItems = filtered.slice(indexOfFirstItemListeFamilles, indexOfLastItemListeFamilles);

                        return (
                          <>
                            {currentItems.length === 0 ? (
                              <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                                  Aucune famille trouvée
                                </td>
                              </tr>
                            ) : (
                              currentItems.map(famille => (
                                <tr key={famille.id}>
                                  <td data-label="Numéro Famille:">{famille.numero_famille}</td>
                                  <td data-label="Nom de Famille:">{famille.nom_famille || '-'}</td>
                                  <td data-label="Chef de Famille:">{famille.chef_famille_nom || '-'}</td>
                                  <td data-label="Téléphone:">{famille.telephone || '-'}</td>
                                  <td data-label="Commune:">{famille.commune || '-'}</td>
                                  <td data-label="Quartier:">{famille.quartier || '-'}</td>
                                  <td data-label="Date création:">{new Date(famille.date_creation).toLocaleDateString('fr-FR')}</td>
                                  <td>
                                    <button
                                      onClick={() => handleImprimerBonTraitement(famille.id, 'famille')}
                                      className="btn btn-sm btn-primary"
                                      title="Imprimer le bon de traitement"
                                    >
                                      🖨️ Bon de Traitement
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {(() => {
                  const filtered = familles.filter(famille => {
                    if (!searchTermListe) return true;
                    const search = searchTermListe.toLowerCase();
                    return (
                      famille.numero_famille?.toLowerCase().includes(search) ||
                      famille.nom_famille?.toLowerCase().includes(search) ||
                      famille.chef_famille_nom?.toLowerCase().includes(search) ||
                      famille.telephone?.includes(search) ||
                      famille.commune?.toLowerCase().includes(search) ||
                      famille.quartier?.toLowerCase().includes(search) ||
                      famille.centre_sante?.toLowerCase().includes(search)
                    );
                  });
                  const totalPagesListeFamilles = Math.ceil(filtered.length / itemsPerPage);
                  const indexOfLastItemListeFamilles = currentPageListeFamilles * itemsPerPage;
                  const indexOfFirstItemListeFamilles = indexOfLastItemListeFamilles - itemsPerPage;

                  if (filtered.length > itemsPerPage) {
                    return (
                      <div className="pagination-container">
                        <div className="pagination-info">
                          Affichage de {indexOfFirstItemListeFamilles + 1} à {Math.min(indexOfLastItemListeFamilles, filtered.length)} sur {filtered.length} familles
                        </div>
                        <div className="pagination-controls">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setCurrentPageListeFamilles(prev => Math.max(1, prev - 1))}
                            disabled={currentPageListeFamilles === 1}
                          >
                            ← Précédent
                          </button>
                          <div className="pagination-numbers">
                            {Array.from({ length: totalPagesListeFamilles }, (_, i) => i + 1).map(page => {
                              if (
                                page === 1 ||
                                page === totalPagesListeFamilles ||
                                (page >= currentPageListeFamilles - 2 && page <= currentPageListeFamilles + 2)
                              ) {
                                return (
                                  <button
                                    key={page}
                                    className={`btn btn-sm ${currentPageListeFamilles === page ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setCurrentPageListeFamilles(page)}
                                  >
                                    {page}
                                  </button>
                                );
                              } else if (page === currentPageListeFamilles - 3 || page === currentPageListeFamilles + 3) {
                                return <span key={page} className="pagination-ellipsis">...</span>;
                              }
                              return null;
                            })}
                          </div>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setCurrentPageListeFamilles(prev => Math.min(totalPagesListeFamilles, prev + 1))}
                            disabled={currentPageListeFamilles === totalPagesListeFamilles}
                          >
                            Suivant →
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </div>
        )}

        {activeTab === 'enfants' && (
          <div className="enfants-section">
            <div className="section-header">
              <h2>👶 Gestion des Enfants des Membres</h2>
            </div>

            {!selectedMembreEnfants ? (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un membre (nom, prénom, matricule)..."
                    value={searchTermEnfants}
                    onChange={(e) => {
                      setSearchTermEnfants(e.target.value);
                      setCurrentPageEnfants(1); // Réinitialiser à la page 1 lors de la recherche
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {(() => {
                  const filteredMembres = membres.filter(membre => {
                    if (!searchTermEnfants) return true;
                    const search = searchTermEnfants.toLowerCase();
                    return (
                      (membre.nom && membre.nom.toLowerCase().includes(search)) ||
                      (membre.postnom && membre.postnom.toLowerCase().includes(search)) ||
                      (membre.prenom && membre.prenom.toLowerCase().includes(search)) ||
                      (membre.numero_matricule && membre.numero_matricule.toLowerCase().includes(search))
                    );
                  });

                  const totalPagesEnfants = Math.ceil(filteredMembres.length / itemsPerPage);
                  const indexOfLastItemEnfants = currentPageEnfants * itemsPerPage;
                  const indexOfFirstItemEnfants = indexOfLastItemEnfants - itemsPerPage;
                  const currentMembres = filteredMembres.slice(indexOfFirstItemEnfants, indexOfLastItemEnfants);

                  return (
                    <>
                      <div className="table-wrapper">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Matricule</th>
                              <th>Nom</th>
                              <th>Postnom</th>
                              <th>Prénom</th>
                              <th>Date adhésion</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentMembres.length > 0 ? (
                              currentMembres.map((membre, index) => (
                                <tr key={membre.id}>
                                  <td>{indexOfFirstItemEnfants + index + 1}</td>
                                  <td>{membre.numero_matricule || '-'}</td>
                                  <td>{membre.nom || '-'}</td>
                                  <td>{membre.postnom || '-'}</td>
                                  <td>{membre.prenom || '-'}</td>
                                  <td>{membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                                  <td>
                                    <button
                                      className="btn btn-primary"
                                      onClick={() => handleSelectMembreEnfants(membre)}
                                      style={{ padding: '5px 10px', fontSize: '12px' }}
                                    >
                                      👶 Gérer les enfants
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                  {searchTermEnfants ? 'Aucun membre trouvé pour cette recherche.' : 'Aucun membre enregistré.'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {filteredMembres.length > itemsPerPage && (
                        <div className="pagination-container">
                          <div className="pagination-info">
                            Affichage de {indexOfFirstItemEnfants + 1} à {Math.min(indexOfLastItemEnfants, filteredMembres.length)} sur {filteredMembres.length} membre(s)
                          </div>
                          <div className="pagination-controls">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => setCurrentPageEnfants(prev => Math.max(1, prev - 1))}
                              disabled={currentPageEnfants === 1}
                            >
                              ← Précédent
                            </button>
                            <div className="pagination-numbers">
                              {Array.from({ length: totalPagesEnfants }, (_, i) => i + 1).map(page => {
                                if (
                                  page === 1 ||
                                  page === totalPagesEnfants ||
                                  (page >= currentPageEnfants - 2 && page <= currentPageEnfants + 2)
                                ) {
                                  return (
                                    <button
                                      key={page}
                                      className={`btn btn-sm ${currentPageEnfants === page ? 'btn-primary' : 'btn-secondary'}`}
                                      onClick={() => setCurrentPageEnfants(page)}
                                    >
                                      {page}
                                    </button>
                                  );
                                } else if (page === currentPageEnfants - 3 || page === currentPageEnfants + 3) {
                                  return <span key={page} className="pagination-ellipsis">...</span>;
                                }
                                return null;
                              })}
                            </div>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => setCurrentPageEnfants(prev => Math.min(totalPagesEnfants, prev + 1))}
                              disabled={currentPageEnfants === totalPagesEnfants}
                            >
                              Suivant →
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>
                    Enfants de {selectedMembreEnfants.nom} {selectedMembreEnfants.prenom} 
                    ({enfantsMembre.length})
                  </h3>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedMembreEnfants(null);
                      setEnfantsMembre([]);
                      setNouvelEnfant({ nom: '', prenom: '', date_naissance: '', lien: '' });
                      setPhotoEnfant(null);
                      setWebcamKeyEnfant(prev => prev + 1);
                    }}
                  >
                    ← Retour à la liste
                  </button>
                </div>

                {/* Formulaire pour ajouter un enfant */}
                <div style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd' }}>
                  <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>Ajouter un enfant</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Nom *</label>
                      <input
                        type="text"
                        value={nouvelEnfant.nom}
                        onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, nom: e.target.value })}
                        placeholder="Nom"
                        required
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Prénom *</label>
                      <input
                        type="text"
                        value={nouvelEnfant.prenom}
                        onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, prenom: e.target.value })}
                        placeholder="Prénom"
                        required
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Date de naissance</label>
                      <input
                        type="date"
                        value={nouvelEnfant.date_naissance}
                        onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, date_naissance: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Lien</label>
                      <select
                        value={nouvelEnfant.lien}
                        onChange={(e) => setNouvelEnfant({ ...nouvelEnfant, lien: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        <option value="">Sélectionner</option>
                        <option value="Épouse">Épouse</option>
                        <option value="Fils">Fils</option>
                        <option value="Fille">Fille</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <WebcamCapture
                      key={`enfant-photo-${webcamKeyEnfant}`}
                      onCapture={handlePhotoEnfantCapture}
                      label="Photo de l'enfant"
                      fieldName="photo"
                      photoType="passeport"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddEnfantMembreSubmit}
                    className="btn btn-primary"
                    style={{ padding: '10px 20px' }}
                  >
                    ➕ Ajouter
                  </button>
                </div>

                {/* Liste des enfants existants */}
                {enfantsMembre.length > 0 ? (
                  <div className="table-wrapper">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Photo</th>
                          <th>Nom</th>
                          <th>Prénom</th>
                          <th>Date de naissance</th>
                          <th>Lien</th>
                          <th>Date d'enregistrement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enfantsMembre.map((enfant) => (
                          <tr key={enfant.id}>
                            <td>
                              {enfant.photo ? (
                                <img 
                                  src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${enfant.photo}`} 
                                  alt={`${enfant.nom} ${enfant.prenom}`}
                                  style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'inline';
                                    }
                                  }}
                                />
                              ) : null}
                              {!enfant.photo && (
                                <span style={{ color: '#999', fontSize: '12px' }}>Pas de photo</span>
                              )}
                            </td>
                            <td>{enfant.nom}</td>
                            <td>{enfant.prenom}</td>
                            <td>{enfant.date_naissance || '-'}</td>
                            <td>{enfant.lien || '-'}</td>
                            <td>{enfant.created_at ? new Date(enfant.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontStyle: 'italic', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd' }}>
                    Aucun enfant enregistré pour ce membre
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedMembre && (
        <CarteMembre
          membre={selectedMembre}
          onClose={() => setSelectedMembre(null)}
        />
      )}

      {selectedFamille && (
        <CarteFamille
          famille={selectedFamille}
          onClose={() => setSelectedFamille(null)}
        />
      )}

      {selectedPersonneBon && typePersonneBon && (
        <BonTraitement
          personne={selectedPersonneBon}
          type={typePersonneBon}
          cotisations={cotisationsBon}
          onClose={() => {
            setSelectedPersonneBon(null);
            setTypePersonneBon(null);
            setCotisationsBon([]);
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default DashboardGerer;

